/**
 * Table: ScrollablePane + DetailsList, columns, sticky header, selection.
 */
import React, { useMemo } from "react";
import {
  Stack,
  ScrollablePane,
  ScrollbarVisibility,
  Sticky,
  StickyPositionType,
  ConstrainMode,
  Icon,
} from "@fluentui/react";
import {
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
  DetailsRowCheck,
  DetailsRow,
} from "@fluentui/react/lib/DetailsList";
import { mergeStyleSets } from "@fluentui/react/lib/Styling";
import { CheckInNewRowActions } from "./CheckInNewRowActions";

const classNames = mergeStyleSets({
  wrapper: {
    position: "relative",
    maxHeight: "inherit",
    overflowY: "hidden",
  },
  fileIconImg: {
    width: 16,
    height: 16,
    display: "block",
    flexShrink: 0,
  },
  nameCell: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    minWidth: 0,
    width: "100%",
    overflow: "hidden",
  },
  folderName: {
    cursor: "pointer",
    selectors: {
      ":hover": {
        textDecoration: "underline",
      },
    },
  },
  rowCheckWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
  },
});

/**
 * @param {{
 *   items: Array<object>,
 *   selectedByKey: Record<string, object>,
 *   onToggleRowSelection: (item: object, checked: boolean) => void,
 *   onToggleAllVisible: (checked: boolean) => void,
 *   theme: object,
 *   gridThemeStyle: object,
 *   getMenuProps: (item: object) => object,
 *   onFolderClick?: (item: object) => void,
 *   onOpenInIFS?: (item: object) => void,
 * }} props
 */
export function CheckInNewDocumentsTable({
  items,
  selectedByKey = {},
  onToggleRowSelection,
  onToggleAllVisible,
  theme,
  gridThemeStyle,
  getMenuProps,
  onFolderClick,
  onOpenInIFS,
}) {
  const isDarkMode =
    String(theme.colors.pureColor || "").toLowerCase() === "#000000";
  const scrollbarTrack =
    theme.colors.scrollbarBackground ?? (isDarkMode ? "#525252" : "#fff");
  const scrollbarThumb =
    theme.colors.scrollbarThumb ?? (isDarkMode ? "#848484" : "#ccc");
  const sbWidth = 10;
  const sbHeight = 10;

  const visibleItemKeys = useMemo(
    () =>
      items
        .map((item) => String(item?.key ?? ""))
        .filter(Boolean),
    [items]
  );

  const renderFileIcon = (item) =>
    item.isFolder ? (
      <Icon
        iconName="FabricFolderFill"
        className={classNames.fileIconImg}
        styles={{ root: { color: "#FFCE44", fontSize: 16, width: 16, height: 16 } }}
        aria-label="folder"
      />
    ) : (
      <img
        src={item.iconName}
        className={classNames.fileIconImg}
        alt={item.fileType ? `${item.fileType} icon` : "file"}
      />
    );

  const columns = useMemo(
    () => [
      {
        key: "__select",
        name: "",
        fieldName: "__select",
        minWidth: 44,
        maxWidth: 44,
        isResizable: false,
        isIconOnly: true,
        ariaLabel: "Toggle selection",
        onRenderHeader: () => {
          const selectedOnPage = visibleItemKeys.filter((key) => selectedByKey[key]).length;
          const allSelected =
            visibleItemKeys.length > 0 && selectedOnPage === visibleItemKeys.length;
          const indeterminate =
            selectedOnPage > 0 && selectedOnPage < visibleItemKeys.length;
          const nextChecked = indeterminate ? true : !allSelected;

          return (
            <div className={classNames.rowCheckWrapper}>
              <DetailsRowCheck
                ariaLabel="Toggle selection for all items"
                canSelect={visibleItemKeys.length > 0}
                isHeader={true}
                isVisible={true}
                selected={allSelected}
                anySelected={selectedOnPage > 0}
                isIndeterminate={indeterminate}
                onClick={(ev) => {
                  ev?.preventDefault?.();
                  ev?.stopPropagation?.();
                  onToggleAllVisible(nextChecked);
                }}
              />
            </div>
          );
        },
        onRender: (item, index) => {
          const key = String(item?.key ?? "");
          const checked = !!(key && selectedByKey[key]);
          return (
            <div className={classNames.rowCheckWrapper}>
              <DetailsRowCheck
                ariaLabel="Toggle selection"
                canSelect={true}
                isVisible={true}
                selected={checked}
                anySelected={Object.keys(selectedByKey).length > 0}
                onClick={(ev) => {
                  ev?.preventDefault?.();
                  ev?.stopPropagation?.();
                  onToggleRowSelection(item, !checked);
                }}
              />
            </div>
          );
        },
      },
      {
        key: "column2",
        name: "Name",
        fieldName: "name",
        minWidth: 200,
        maxWidth: 400,
        isResizable: true,
        onRender: (item) => {
          const nameStyle = {
            flex: 1,
            minWidth: 0,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontSize: theme.fontSizes.normal,
            fontFamily: theme.fonts.regular,
            fontWeight: theme.fontWeight.normal,
            fontStyle: "normal",
            lineHeight: `${theme.lineHeight.grid}px`,
            color: theme.colors.themeFontColor,
          };
          const nameLabel =
            item.isFolder && onFolderClick ? (
              <span
                role="link"
                tabIndex={0}
                className={classNames.folderName}
                style={nameStyle}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onFolderClick(item);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    event.stopPropagation();
                    onFolderClick(item);
                  }
                }}
              >
                {item.name}
              </span>
            ) : (
              <span style={nameStyle}>{item.name}</span>
            );
          return (
            <div className={classNames.nameCell}>
              {renderFileIcon(item)}
              {nameLabel}
            </div>
          );
        },
      },
      {
        key: "moreOptions",
        ariaLabel: "More options",
        minWidth: 50,
        maxWidth: 50,
        isResizable: false,
        onRender: (item) => (
          <div
            style={{
              display: "flex",
              width: "100%",
              height: "100%",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CheckInNewRowActions
              item={item}
              menuProps={getMenuProps(item)}
              theme={theme}
            />
          </div>
        ),
      },
      {
        key: "column3",
        name: "Modified",
        fieldName: "modified",
        minWidth: 100,
        maxWidth: 200,
        isResizable: true,
      },
      {
        key: "column4",
        name: "Modified By",
        fieldName: "modifiedby",
        minWidth: 100,
        maxWidth: 200,
        isResizable: true,
      },
      {
        key: "column5",
        name: "Document No",
        fieldName: "docNo",
        minWidth: 100,
        maxWidth: 200,
        isResizable: true,
        onRender: (item) => {
          if (!onOpenInIFS || !item.docNo) {
            return <span>{item.docNo}</span>;
          }
          return (
            <span
              onClick={(ev) => {
                ev.preventDefault();
                ev.stopPropagation();
                onOpenInIFS(item);
              }}
              style={{
                cursor: "pointer",
                textDecoration: "underline",
                color: theme.colors.themeDarkColor ?? "#249f5d",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {item.docRev ? `${item.docNo} - ${item.docRev}` : item.docNo}
            </span>
          );
        },
      },
    ],
    [
      visibleItemKeys,
      selectedByKey,
      onToggleAllVisible,
      onToggleRowSelection,
      theme,
      getMenuProps,
      onFolderClick,
      onOpenInIFS,
    ]
  );

  const minTableWidth = useMemo(
    () =>
      columns.reduce(
        (sum, col) => sum + (col.minWidth ?? col.maxWidth ?? 0),
        0,
      ),
    [columns],
  );

  const detailsListStyles = useMemo(
    () => ({
      ...gridThemeStyle.detailListStyle,
      root: [
        gridThemeStyle.detailListStyle?.root,
        {
          minWidth: minTableWidth > 0 ? minTableWidth : undefined,
          overflowX: "visible",
        },
      ],
    }),
    [gridThemeStyle.detailListStyle, minTableWidth],
  );

  return (
    <Stack.Item
      grow
      className={classNames.wrapper}
      style={{ overflow: "hidden", minHeight: 0 }}
      styles={{
        root: {
          background: theme.colors.backgroundColor,
          flex: 1,
          minHeight: 0,
        },
      }}
    >
      <ScrollablePane
        scrollbarVisibility={ScrollbarVisibility.auto}
        styles={{
          root: {
            overflowX: "auto",
            selectors: {
              ".ms-ScrollablePane--contentContainer": {
                background: theme.colors.backgroundColor,
                overflowX: "auto",
                scrollbarWidth: "thin",
                scrollbarColor: `${scrollbarThumb} ${scrollbarTrack}`,
                "-ms-overflow-style": "auto",
              },
              ".ms-ScrollablePane--contentContainer::-webkit-scrollbar": {
                width: sbWidth,
                height: sbHeight,
                display: "block",
              },
              ".ms-ScrollablePane--contentContainer::-webkit-scrollbar-track": {
                background: scrollbarTrack,
              },
              ".ms-ScrollablePane--contentContainer::-webkit-scrollbar-thumb": {
                background: scrollbarThumb,
                borderRadius: sbWidth,
                border: `2px solid ${scrollbarTrack}`,
                minHeight: 40,
              },
              ".ms-ScrollablePane--contentContainer::-webkit-scrollbar-thumb:hover":
                {
                  background: isDarkMode ? "#9a9a9a" : "#b3b3b3",
                },
              ".ms-ScrollablePane--contentContainer::-webkit-scrollbar-button":
                {
                  display: "none",
                  height: 0,
                  width: 0,
                },
            },
          },
        }}
      >
        <DetailsList
          styles={detailsListStyles}
          items={items}
          compact={false}
          columns={columns}
          selectionMode={SelectionMode.none}
          getKey={(item) => String(item?.key ?? "")}
          layoutMode={DetailsListLayoutMode.fixedColumns}
          constrainMode={ConstrainMode.unconstrained}
          isHeaderVisible={true}
          selectionPreservedOnEmptyClick={true}
          onRenderRow={(rowProps) => {
            if (!rowProps) return null;
            const key = String(rowProps.item?.key ?? "");
            const checked = !!(key && selectedByKey[key]);
            return (
              <DetailsRow
                {...rowProps}
                className={`${rowProps.className ?? ""}${checked ? " is-selected" : ""}`}
                onClick={(ev) => {
                  const target = ev?.target;
                  const inCheck =
                    target?.closest?.('[data-automationid="DetailsRowCheck"]') ||
                    target?.closest?.(`.${classNames.rowCheckWrapper}`);
                  if (inCheck) return;
                  if (target?.closest?.(`.${classNames.folderName}`)) return;

                  ev?.preventDefault?.();
                  onToggleRowSelection(rowProps.item, !checked);
                }}
              />
            );
          }}
          ariaLabelForSelectionColumn="Toggle selection"
          ariaLabelForSelectAllCheckbox="Toggle selection for all items"
          checkButtonAriaLabel="Select row"
          onRenderDetailsHeader={(headerProps, defaultRender) => (
            <Sticky
              stickyPosition={StickyPositionType.Header}
              isScrollSynced={true}
              stickyBackgroundColor="transparent"
            >
              {defaultRender({
                ...headerProps,
                styles: gridThemeStyle.headerStyle,
              })}
            </Sticky>
          )}
        />
      </ScrollablePane>
    </Stack.Item>
  );
}
